import React, { useEffect, useState, useMemo, useCallback } from "react";
import Theme from "@adsk/alloy-react-theme";
import { displayDate, displayTime } from "@adsk/alloy-react-datetime";
import { PhotoActivityCard } from "@adsk/alloy-react-activity-log";
import Button from "@adsk/alloy-react-button";
import styled from "styled-components";
import { ActivityLogCardProps } from "../activityLogTypes";
import { useSdk } from "@adsk/acc-issues-core/state/sdk/useSdk";
import { ThumbnailImageDisplay } from "../../IssueDetailTabPanel/detail-panel-fields/ThumbnailField";
import { getIssueById } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import { trackIssueThumbnailOpenFromActivityPanel } from "../../../utils/analytics/issueActionsTracking";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { useSetSnapshotAsAThumbnail } from "../../IssueDetailTabPanel/hooks/IssueSnapshot/useSetSnapshotAsAThumbnail";
import { IssueSnapshotCreateTrigger } from "../../../utils/analytics/analyticsLib093IssuesEvents";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { IssueDetailPanelTabs } from "../../IssueDetailPanelTabType";
import { useActiveDetailPanelTab } from "../../ActiveDetailPanelTabContext";
import { useSnapshotAbortController } from "../../IssueDetailTabPanel/hooks/IssueSnapshot/useSnapshotAbortController";
import { is2dFileIssue } from "../../../utils/data/issueLinkedDocumentsUtils";
import usePlacementData from "../../IssueDetailTabPanel/hooks/PlacementFieldHooks/usePlacementData";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";

import { useIssuePlacementType } from "@adsk/acc-issues-core/utils/hooks/useIssuePlacementType";

const SnapshotImageContainer = styled.div`
  max-height: 160px;
`;

const SetAsThumbnailButton = styled(Button)`
  position: absolute;
  bottom: 21px;
  right: 23px;
`;

export const ChangedSnapshotActivityCard = ({
  commonActivityProps,
}: {
  commonActivityProps: ActivityLogCardProps;
}) => {
  const [isSetButtonLoading, setIsSetButtonLoading] = useState(false);
  const { activityChange, user, date } = commonActivityProps;
  const { showNotification } = useNotifications();
  const { T, locale } = useTranslations();
  const [url, setUrl] = useState("");
  const sdk = useSdk();
  const getIssue = useSelector(getIssueById);
  const issue = getIssue(activityChange.object.id);
  const setSnapshotAsThumbnail = useSetSnapshotAsAThumbnail();
  const { getFlowId } = useSnapshotAbortController();
  const [, setIssueDetailPanelTab] = useActiveDetailPanelTab();
  const getPlacementData = usePlacementData();
  const issuePlacementData = getPlacementData(issue);
  const { sheetAssociation, isSheetAssociationFetched } = issuePlacementData;
  const getIssuePlacementType = useIssuePlacementType();
  const placementType = getIssuePlacementType(issue);

  const handleThumbnailClick = useCallback(() => {
    trackIssueThumbnailOpenFromActivityPanel(placementType);
  }, [placementType]);

  const { value: shouldAllowThumbnailFor2DIssue } = useFeatureFlag(
    FeatureFlag.IssueThumbnailOf2dSnapshots
  );

  const is2DSheet = isSheetAssociationFetched && sheetAssociation;

  const shouldRenderSnapshotViewActionButtons = useMemo(() => {
    if (!isAttributeEditable(issue, "snapshotUrn")) {
      return false;
    }

    if (is2DSheet || is2dFileIssue(issue)) {
      // For 2D issues, need BOTH the button FF AND the 2D FF to show the button
      return shouldAllowThumbnailFor2DIssue;
    }

    // For non-2D issues: (3D or no placement, but not sheet/2D file)
    return (
      isSheetAssociationFetched && !sheetAssociation && !is2dFileIssue(issue)
    );
  }, [
    issue,
    is2DSheet,
    isSheetAssociationFetched,
    sheetAssociation,
    shouldAllowThumbnailFor2DIssue,
  ]);

  const activityChangeAfter = useMemo(
    () => JSON.parse(activityChange.after),
    [activityChange.after]
  );

  const { urn, hasMarkups } = activityChangeAfter;

  const shouldDisableSetThumbnailButton = useMemo(() => {
    return urn === issue.snapshotUrn || isSetButtonLoading;
  }, [urn, issue, isSetButtonLoading]);

  useEffect(() => {
    const fetchUrl = async () => {
      if (urn) {
        const { url } = await sdk.Oss.getDownloadLink(urn);
        setUrl(url);
      }
    };
    fetchUrl();
  }, [sdk.Oss, urn]);

  //Copied from Alloy-react codebase, we use this to make sure that the date is displayed in the same format as the activity footer
  //https://git.autodesk.com/BIM360/alloy-react/blob/master/packages/ActivityLog/layout/Footer.tsx
  const snapshotViewDateToDisplay = useMemo(() => {
    if (!date) return "";
    const dateString = displayDate(date, {
      locale,
      format: "numeric",
      utc: false,
    });
    const timeString = `${T.translate(
      "thumbnailField.snapshotView.timePrefix"
    )} ${displayTime(date, {
      locale,
      format: "numeric",
      utc: false,
    })}`;

    return `${dateString} ${timeString}`;
  }, [T, locale, date]);

  const snapshotViewHeader = () => {
    return (
      <div>
        {T.translate("thumbnailField.snapshotView.title")} #{issue.displayId}
        <p style={{ ...Theme.typography.heading3, marginBottom: 0 }}>
          {T.translate("thumbnailField.snapshotView.snapshotActivityTitle", {
            name: user.name,
            date: snapshotViewDateToDisplay,
          })}
        </p>
      </div>
    );
  };

  const handleRestoreThumbnail = useCallback(async () => {
    setIsSetButtonLoading(true);
    try {
      if (
        (!is2DSheet && !is2dFileIssue(issue)) ||
        shouldAllowThumbnailFor2DIssue
      ) {
        await setSnapshotAsThumbnail({
          issue,
          storageUrn: urn,
          hasMarkups,
          trigger: IssueSnapshotCreateTrigger.SET_THUMBNAIL_BUTTON,
          flowId: getFlowId(issue.id),
          handleFailureNotification: () => {
            showNotification({
              status: NOTIFICATION_STATUSES.ERROR,
              message: T.translate(
                "thumbnailField.snapshotView.failureMessage"
              ),
              dismissible: true,
            });
          },
          onSuccess: async () => {
            showNotification({
              status: NOTIFICATION_STATUSES.SUCCESS,
              message: T.translate(
                "thumbnailField.snapshotView.successMessage"
              ),
              dismissible: true,
            });
            setIssueDetailPanelTab(IssueDetailPanelTabs.Details);
          },
        });
      }
    } finally {
      setIsSetButtonLoading(false);
    }
  }, [
    is2DSheet,
    issue,
    shouldAllowThumbnailFor2DIssue,
    setSnapshotAsThumbnail,
    urn,
    hasMarkups,
    getFlowId,
    showNotification,
    T,
    setIssueDetailPanelTab,
  ]);

  const renderSetAsThumbnailButton = () => {
    return (
      <SetAsThumbnailButton
        variant={Button.VARIANTS.PRIMARY}
        onClick={handleRestoreThumbnail}
        loading={isSetButtonLoading}
        disabled={shouldDisableSetThumbnailButton}
        data-testid="set-as-thumbnail-button"
      >
        {T.translate("thumbnailField.snapshotView.restoreThumbnail")}
      </SetAsThumbnailButton>
    );
  };
  return (
    <PhotoActivityCard
      className="IssuesActivityLog__activity-card"
      {...commonActivityProps}
      label=""
    >
      <SnapshotImageContainer data-testid="activity-snapshot-image-container">
        <ThumbnailImageDisplay
          url={url}
          issue={issue}
          renderViewHeader={snapshotViewHeader}
          onThumbnailClick={handleThumbnailClick}
          renderSnapshotViewActionButtons={
            shouldRenderSnapshotViewActionButtons
              ? renderSetAsThumbnailButton
              : undefined
          }
        />
      </SnapshotImageContainer>
    </PhotoActivityCard>
  );
};
